import { dayjs } from '@/components/dispatch/lib/datetime'
import { fromZonedTime } from 'date-fns-tz'
import { resolveBusinessTimezone } from '@/utils/businessDay'
import type { Reminder } from './types'

/** "now" in the user's timezone. */
const nowInTz = (tz: string) => dayjs().tz(tz)

export const todayISO = (tz: string): string => nowInTz(tz).format('YYYY-MM-DD')
export const addDaysISO = (n: number, tz: string): string => nowInTz(tz).add(n, 'day').format('YYYY-MM-DD')
export const nowHHMM = (tz: string): string => nowInTz(tz).format('HH:mm')

/** Next Monday's date (strictly future; if today is Monday, +7). */
export const nextMondayISO = (tz: string): string => {
  const d = nowInTz(tz)
  const dow = d.day() // 0=Sun..6=Sat
  const add = ((8 - dow) % 7) || 7
  return d.add(add, 'day').format('YYYY-MM-DD')
}

export const isDueNow = (r: Reminder, tz: string): boolean => {
  if (r.status === 'done') return false
  const today = todayISO(tz)
  if (r.dueDate < today) return true
  if (r.dueDate === today) return !r.dueTime || r.dueTime <= nowHHMM(tz)
  return false
}

/** 离 dueDate dueTime（用户时区的钟点）还有多少毫秒；已过为负。 */
export const msUntilDue = (r: { dueDate: string; dueTime: string }, tz: string, now: Date = new Date()): number =>
  fromZonedTime(`${r.dueDate}T${r.dueTime}:00`, resolveBusinessTimezone(tz)).getTime() - now.getTime()

export type ReminderGroup = 'today' | 'tomorrow' | 'week' | 'later'

export const groupOf = (r: Reminder, tz: string): ReminderGroup => {
  const today = todayISO(tz)
  const tomorrow = addDaysISO(1, tz)
  const weekEnd = addDaysISO(7, tz)
  if (r.dueDate <= today) return 'today'
  if (r.dueDate === tomorrow) return 'tomorrow'
  if (r.dueDate > tomorrow && r.dueDate <= weekEnd) return 'week'
  return 'later'
}

export type SnoozeKind = '1h' | 'evening' | 'tomorrow'

export const snoozeTarget = (kind: SnoozeKind, tz: string): { dueDate: string; dueTime: string } => {
  const now = nowInTz(tz)
  if (kind === '1h') {
    const t = now.add(1, 'hour')
    return { dueDate: t.format('YYYY-MM-DD'), dueTime: t.format('HH:mm') }
  }
  if (kind === 'evening') {
    if (now.hour() >= 18) return { dueDate: addDaysISO(1, tz), dueTime: '09:00' }
    return { dueDate: todayISO(tz), dueTime: '18:00' }
  }
  return { dueDate: addDaysISO(1, tz), dueTime: '09:00' }
}

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const shortDate = (iso: string): string => {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  return `${WD[dt.getDay()]}, ${MO[dt.getMonth()]} ${dt.getDate()}`
}
export const fmtTime = (hhmm: string | null): string => {
  if (!hhmm) return ''
  const [h0, m] = hhmm.split(':').map(Number)
  const ap = h0 >= 12 ? 'PM' : 'AM'
  const h = ((h0 + 11) % 12) + 1
  return `${h}:${String(m).padStart(2, '0')} ${ap}`
}

/** now + offsetMinutes, in the user's tz, rolling the date past midnight. */
export const relativeTarget = (offsetMinutes: number, tz: string): { dueDate: string; dueTime: string } => {
  const t = nowInTz(tz).add(offsetMinutes, 'minute')
  return { dueDate: t.format('YYYY-MM-DD'), dueTime: t.format('HH:mm') }
}

/** Combine an ISO date with an HH:mm time. */
export const atTimeOn = (dateISO: string, hhmm: string): { dueDate: string; dueTime: string } => {
  return { dueDate: dateISO, dueTime: hhmm }
}

/** Current HH:mm in the user's tz (for the "Same time" shortcut). */
export const sameTimeNow = (tz: string): string => nowInTz(tz).format('HH:mm')

/**
 * 归一化后端返回的 reminder:due_time 由 Pydantic 序列化为 "HH:mm:ss",
 * 而 isDueNow/排序全是字符串比较('14:30:00' > '14:30' 会晚 1 分钟才判到期),
 * 入 Redux 前统一裁成 "HH:mm"。search 与 create 两条响应路径都必须过这里。
 */
export const normalizeReminder = (r: Reminder): Reminder =>
  r.dueTime ? { ...r, dueTime: r.dueTime.slice(0, 5) } : r
